import {
    TCustomerDetails,
    TCustomerListing,
    TGetCustomerListingFilters,
    TCustomerFilter,
    TCustomerInventory,
    TGetCollateralListing,
    TPendingCollateralApprovalListingFilters,
    TCreateCollateralReduxParams,
    TDeleteCustomerCollateralReduxParams,
    TCreateReleaseEarmarkReduxParams,
} from '~types/feat/customer';

import {
    TBuyOrSellMultipleGoldParams,
    TCreateInventoryParamsV2,
    TCreateTransactionDraftResponse,
    TGetInventoryFilterParams,
    TGetInventoryListing,
    TGetInventoryListingFilters,
    TGetInventoryTransactionListingFilters,
    TGetInventoryTransferListingFilters,
    TGetPendingGoldConversionFiltersRedux,
    TGetSerialNumberFilterParams,
    TGetTransactionListingFilters,
    TGoldConversionListingData,
    TGoldConversionRedux,
    TGoldPrices,
    TGoldTransactionListing,
    TInventoryFilter,
    TInventoryTransactionListing,
    TInventoryTransferListing,
    TProductFilter,
    TSerialNumberFilter,
    TSerialNumberListingReduxParams,
    TTransactionListing,
    TTransferInventoryParams,
    TGetEarmarkConversionListingFilters,
    TGetEarmarkConversionListingAPIResponse,
    TInventoryTransferData,
    TGetBranchMarketStatusBranchAPIResponse,
} from '~types/feat/gold';

import {
    TBranch,
    TRegion,
    TGetBranchListingFilters,
    TBranchFilter,
    TRegionFilter,
    TBranchChangeHistoryData,
} from '~types/feat/branch';

import {
    ApprovalTabsEnum,
    MarketStatusEnum,
    MetalTypeEnum,
    NavigationTabsEnum,
    SettingTabsEnum,
    TransactionActionEnum,
    TransactionStatusEnum,
} from '~utils/enums';

import { TAPIKeySettingsReduxResponse, TDiscountDetailsSettings, TDiscountListingFilters, TGetProductChangeHistoryListing, TGetProjectChangeHistoryListing, TGetInventoryTransferListing, TGlobalAndProductSettings, TGetReleaseEarmarkListing } from '~types/feat/setting';
import {
    TAdminDetails,
    TAdminListing,
    TExportAdminRoleListingAPIResponse,
    TExportAdminUserListingAPIResponse,
    TGetAdminListingFilters,
    TRoleFilter,
    TRoleListing,
} from '~types/feat/admin';

import {
    TDashboardProductInventory,
    TDashboardProductPricing,
    TDashboardSilverProduct,
    TGetDashboardProductInventoryFilters,
} from '~types/feat/dashboard';
import { TUserDetails } from '~types/feat/auth';
import { TPendingDiscountApprovalListingFilters, TPendingDiscountListing, TUpdateCollateralStatusReduxParams, TPendingProjectApprovalListingFilters, TPendingTransfersApprovalListingFilters, TUpdateDiscountStatusReduxParams, TUpdateInventoryTransactionStatusReduxParams, TUpdateProductStatusReduxParams, TUpdateProjectStatusReduxParams, TUpdateTransactionStatusReduxParams, TupdateTransferStatusReduxParams, TPendingReleaseEarmarkListingFilters, TUpdateReleaseEarmarkStatusReduxParams, TUpdateBranchesStatusReduxParams, TUpdateGoldConversionStatusReduxParams, TUpdateEarmarkConversionStatusReduxParams, TGetPendingStockOutListingFilters, TUpdateStockOutStatusReduxParams } from '~types/feat/approval';
import { TGetProjectFilter, TGetProjectListingFilters, TProjectDetails, TProjectListing } from '~types/feat/project';
import { TSilverConversionRedux, TSilverPrices, TBuyOrSellMultipleSilverParams, TSilverTransactionListing, TGetSilverInventoryListingFilters, TGetSilverInventoryListing, TCreateSilverTransactionDraftResponse, TGetSilverTransactionListingFilters, TTransactionSilverListing, TInventorySilverTransactionListing, TSilverInventoryFilter, TSilverProductFilter, TGetSilverInventoryTransactionListingFilters, TSerialNumberSilverListingReduxParams } from '~types/feat/silver';
import { TCreateStockOutParams, TGetStockOutsListingFilters, TGetStockOutsParams, TGetStockOutTransactionsListingParams, TStockOutListing } from '~types/feat/stockOut';

// Auth state
export type TAuthReduxState = {
    loadings: {
        login: boolean;
        startup: boolean;
        logout: boolean;
        getUserDetails: boolean;
        isMarketOpen: boolean;
    };
    isMarketOpen: boolean;
    userDetails: TUserDetails;
    loginTime: string;
    logoutTime: string;
    errors: {
        login: string;
        logout: string;
        getUserDetails: string;
        isMarketOpen: string;
    };
};

export type TDashboardReduxState = {
    loadings: {
        getDashboardProductPricing: boolean;
        getDashboardSilverProductPricing: boolean;
        getDashboardProductInventory: boolean;
        getDashboardGoldProductInventory: boolean;
        getDashboardSilverProductInventory: boolean;
        getDashboardMarketStatus: boolean;
        openDashboardMarket: boolean;
        closeDashboardMarket: boolean;
    };
    dashboardProductPricing: TDashboardProductPricing[];
    dashboardSilverProductPricing: TDashboardSilverProduct[];
    dashboardGoldProductInventoryFilters: TGetDashboardProductInventoryFilters;
    dashboardSilverProductInventoryFilters: TGetDashboardProductInventoryFilters;
    dashboardGoldProductInventory: TDashboardProductInventory[];
    dashboardSilverProductInventory: TDashboardProductInventory[];
    dashboardMarketStatus: MarketStatusReduxState;
    modals: {
        isMarketStatusOpenConfirmationModalOpen: boolean;
        isMarketStatusCloseConfirmationModalOpen: boolean;
    };
    errors: {
        getDashboardProductPricing: string;
        getDashboardSilverProductPricing: string;
        getDashboardProductInventory: string;
        getDashboardGoldProductInventory: string;
        getDashboardSilverProductInventory: string;
        getDashboardMarketStatus: string;
        openDashboardMarket: string;
        closeDashboardMarket: string;
    };
}

export type MarketStatusReduxState = {
    status: MarketStatusEnum;
    currentTime: string;
}

export type MarketOpenMarketStatusRedusState = {
    branchCode: string;
}

export type ReportGoldInventoryTransferRedux = {
    index: number;
    maxIndex: number;
    filters: ReportGoldInventoryReceivedListingFilters;
    data: TInventoryTransferData[];
}

// Customer state
export type TCustomerReduxState = {
    loadings: {
        getCustomerListing: boolean;
        createNewCustomer: boolean;
        editCustomerDetails: boolean;
        deleteCustomer: boolean;
        getCustomerDetails: boolean;
        getCustomerTransactions: boolean;
        getCustomerCollateral: boolean;
        getAllCustomers: boolean;
        getCustomerInventory: boolean;
        exportCustomerListing: boolean;
        createCollateral: boolean;
        getCollateralMarketValue: boolean;
        cancelCustomerCollateral: boolean;
        updateReleaseEarmark: boolean;
    };
    customerListing: {
        index: number;
        maxIndex: number;
        filters: TGetCustomerListingFilters;
        data: TCustomerListing[];
    };
    modals: {
        createCollateralModalIsOpen: boolean;
        createNewCustomerModalIsOpen: boolean;
        customerDetailsModalIsOpen: boolean;
        customerPaperGoldBalanceIsOpen: boolean;
        deleteCustomerModalIsOpen: boolean;
        deleteCustomerCollateralModalIsOpen: boolean
        isEditing: boolean;
        collateralDetailsModalIsOpen: boolean;
        isCreateCollateralFormFilled: boolean;
        confirmReleaseEarmarkModalIsOpen: boolean;
    };
    customerDetails: TCustomerDetails;
    customerTransactions: {
        index: number;
        maxIndex: number;
        data: TTransactionListing[];
    };
    customerCollateral:{
        index: number;
        maxIndex: number;
        data: TGetCollateralListing[];
    };
    allCustomers: TCustomerFilter[];
    customerInventory: TCustomerInventory[];
    customerListingForExport: TCustomerListing[];
    createCollateralParams: TCreateCollateralReduxParams;
    tempCollateralState: TDeleteCustomerCollateralReduxParams;
    tempReleaseEarmarkState: TCreateReleaseEarmarkReduxParams;
    selectedCollateralMetalType: MetalTypeEnum,
    errors: {
        getCustomerListing: string;
        createNewCustomer: string;
        editCustomerDetails: string;
        deleteCustomer: string;
        getCustomerDetails: string;
        getCustomerTransactions: string;
        getCustomerCollateral: string;
        getAllCustomers: string;
        getCustomerInventory: string;
        exportCustomerListing: string;
        createCollateral: string;
        getCollateralMarketValue: string;
        cancelCustomerCollateral: string;
        updateReleaseEarmark: string;
    };
}

// Gold state
export type TGoldReduxState = {
    loadings: {
        getAllProducts: boolean;
        getAllInventories: boolean;
        getAllSerialNumbers: boolean;
        getBranchInventories: boolean;
        getMultipleSerialNumbers: boolean;
        // buy and sell gold
        getTransactionListing: boolean;
        getGoldPrices: boolean;
        createBuyGoldTransaction: boolean;
        createSellGoldTransaction: boolean;
        getTransactionReceipt: boolean;
        updateInventoryTransactionStatus: boolean;
        updateTransactionStatus: boolean;
        getTransactionPricePerGramValue: boolean;
        // gold inventory
        getInventoryTransactionListing: boolean;
        getInventoryListing: boolean;
        createInventory: boolean;
        createTransactionDraft: boolean;
        transferInventory: boolean;
        exportInventoryTransactionListing: boolean;
        exportGoldTransactionListing: boolean;
        getTotalNetValueOrQuantity: boolean;
        // transfer
        getInventoryTransferListing: boolean;
        updateInventoryTransferStatus: boolean;
        getSerialNumberListing: boolean;

        //gold invenroty report
        getGoldInventoryReportReceivedListing: boolean;
        exportInventoryReceivedListing: boolean;
        getGoldInventoryReportDeliveredListing: boolean;
        exportInventoryDeliveredListing: boolean;
        getBranchInventoryListing: boolean;
        //conversion
        getGoldConversionListing: boolean;
        getGoldConversionAmount: boolean;
        createGoldConversion: boolean;
        createEarmarkConversion: boolean;
        getEarmarkConversionListing: boolean;
        updateEarmarkDetails: boolean;
        getInventoryUsage: boolean;
        // Stock Out
        getStockOutListing: boolean;
        createStockOut: boolean;
        getStockOutTransactionsListing: boolean;
        // Market Status
        getBranchMarketStatus: boolean;
    };
    buyAndSellGold: {
        transactionListing: {
            index: number;
            maxIndex: number;
            filters: TGetTransactionListingFilters;
            data: TTransactionListing[];
        };
        goldPrices: TGoldPrices;
        transactionType: TransactionActionEnum;
        selectedTransaction: TTransactionListing | undefined;
    };
    goldInventory: {
        inventoryTransactionListing: {
            index: number;
            maxIndex: number;
            filters: TGetInventoryTransactionListingFilters;
            data: TInventoryTransactionListing[];
        };
        inventoryListing: {
            index: number;
            maxIndex: number;
            filters: TGetInventoryListingFilters;
            data: TGetInventoryListing[];
            selectedTab: GoldSilverInventorySelectedTab;
        };
        inventoryTransferListing: {
            index: number;
            maxIndex: number;
            filters: TGetInventoryTransferListingFilters;
            data: TInventoryTransferListing[];
        };
        inventoryStockOutListing: {
            index: number;
            maxIndex: number;
            totalCount: number,
            filters: TGetStockOutsListingFilters;
            stockOuts: TStockOutListing[];
        };
        inventoryStockOutTransactionsListing: {
            filters?: TGetStockOutTransactionsListingParams;
            affectedTransactions: {
                transactionId: number;
            }[];
            affectedEarmarks: {
                transactionId: number;
            }[];
            affectedConversions: {
                conversionId: number;
            }[];
            affectedCollaterals: {
                collateralId: number;
            }[];
            affectedEarmarkConversions: {
                earmarkConversionId: number;
            }[];
        }
    };
    goldInventoryReport: {
        selectedTab: ReportsGoldInventorySelectedTab;
        branchInventory: ReportBranchInventoryRedux;
        inventoryReceived: ReportGoldInventoryTransferRedux;
        inventoryDelivered: ReportGoldInventoryTransferRedux;
    },
    serialNumber: {
        serialNumberListing: TSerialNumberListingReduxParams;
    };
    allProducts: TProductFilter[];
    allInventories: TInventoryFilter[];
    allSerialNumbers: TSerialNumberFilter[];
    multipleSerialNumbers: TSerialNumberFilter[];
    branchInventories: TInventoryFilter[];
    createTransactionSummary: TCreateTransactionDraftResponse;
    goldBranchMarketStatusDetails: TGetBranchMarketStatusBranchAPIResponse;
    params: {
        buyOrSellGoldParams: TBuyOrSellMultipleGoldParams;
        createInventoryParams: TCreateInventoryParamsV2;
        transferInventoryParams: TTransferInventoryParams;
        inventoryFilterParams: TGetInventoryFilterParams;
        serialNumberFilterParams: TGetSerialNumberFilterParams;
        stockOutsFilterParams: TGetStockOutsParams;
        createStockOutParams: TCreateStockOutParams;
        stockOutTransactionsParams: TGetStockOutTransactionsListingParams;
    }
    goldConversion: TGoldConversionRedux;
    earmarkConversionListing: {
        index: number;
        maxIndex: number;
        filters: TGetEarmarkConversionListingFilters;
        data: TGetEarmarkConversionListingAPIResponse['data'];
    };
    inventoryUsage: {
        totalInventoryStock: number;
        totalInventoryUsed: number;
        totalInventoryBalance: number;
    }
    modals: {
        isFormFilled: boolean;
        isConfirmationModalOpen: boolean;
        isBuyOrSellGoldModalOpen: boolean;
        isTransactionDetailsModalOpen: boolean;
        isInventoryListingModalOpen: boolean;
        isCreateInventoryModalOpen: boolean;
        isTransferInventoryModalOpen: boolean;
        isCreateGoldConversionModalOpen: boolean;
        isCreateStockOutModalOpen: boolean;
        isStockOutTransactionsModalOpen: boolean;
        confirmUpdateInventoryTransactionStatusModalIsOpen: boolean;
        confirmUpdateTransactionStatusModalIsOpen: boolean;
        confirmUpdateInventoryTransfersStatusModalIsOpen: boolean;
        transactionProductListingModalIsOpen: boolean;
    };
    tempUpdateInventoryTransactionState: TUpdateInventoryTransactionStatusReduxParams;
    tempUpdateTransactionState: TUpdateTransactionStatusReduxParams;
    tempUpdateInventoryTransfersState: TupdateTransferStatusReduxParams;

    inventoryTransactionListingForExport: TInventoryTransactionListing[];
    inventoryReceivedListingForExport: TInventoryTransferData[];
    inventoryDeliveredListingForExport: TInventoryTransferData[];
    goldTransactionListingForExport: TGoldTransactionListing[];
    errors: {
        getAllProducts: string;
        getAllInventories: string;
        getAllSerialNumbers: string;
        getBranchInventories: string;
        getMultipleSerialNumbers: string;
        // buy and sell gold
        getTransactionListing: string;
        getGoldPrices: string;
        createBuyGoldTransaction: string;
        createSellGoldTransaction: string;
        getTransactionReceipt: string;
        updateInventoryTransactionStatus: string;
        updateTransactionStatus: string;
        getTransactionPricePerGramValue: string;
        // gold inventory
        getInventoryTransactionListing: string;
        getInventoryListing: string;
        createInventory: string;
        createTransactionDraft: string;
        transferInventory: string;
        exportInventoryTransactionListing: string;
        exportGoldTransactionListing: string;
        getTotalNetValueOrQuantity: string;
        // transfer
        getInventoryTransferListing: string;
        updateInventoryTransferStatus: string;
        getSerialNumberListing: string;
        //gold inventory report
        getGoldInventoryReportReceivedListing: string;
        exportInventoryReceivedListing: string;
        getGoldInventoryReportDeliveredListing: string;
        exportInventoryDeliveredListing: string;
        getBranchInventoryListing: string;
        //conversion
        getGoldConversionListing: string;
        getGoldConversionAmount: string;
        createGoldConversion: string;
        createEarmarkConversion: string;
        getEarmarkConversionListing: string;
        updateEarmarkDetails: string;
        getInventoryUsage: string;
        // Stock Out
        getStockOutListing: string;
        createStockOut: string;
        getStockOutTransactionsListing: string;
        // market status
        getBranchMarketStatus: string;
    };
}

export type ReportGoldInventoryReceivedRedux = {
    index: number,
    maxIndex: number,
    filters: ReportGoldInventoryReceivedListingFilters,
    data: TInventoryTransactionListing[],
}

export type ReportGoldInventoryReceivedListingFilters = {
    branchName: string;
    branchCode: string;
    fundType: string;
    productName: string;
    serialNo: string;
    productId: number;
}

export type ReportBranchInventoryRedux = {
    index: number;
    maxIndex: number;
    filters: {
        branchCode: string;
    };
    data: TGetInventoryListing[];
}

export enum GoldSilverInventorySelectedTab {
    GoldIslamicPhysical = 1,
    GoldIslamicPaper = 2,
    GoldConventionalPhysical = 3,
    GoldConventionalPaper = 4,
    SilverIslamicPhysical = 5,
    SilverIslamicPaper = 6,
    SilverConventionalPhysical = 7,
    SilverConventionalPaper = 8,
}

export enum SilverInventorySelectedTab {
    IslamicPhysical = 1,
    IslamicPaper = 2,
    ConventionalPhysical = 3,
    ConventionalPaper = 4,
}

// Silver state
export type TSilverReduxState = {
    loadings: {
        getAllProducts: boolean;
        getAllInventories: boolean;
        getAllSerialNumbers: boolean;
        getBranchInventories: boolean;
        // buy and sell silver
        getTransactionListing: boolean;
        getSilverPrices: boolean;
        createBuySilverTransaction: boolean;
        createSellSilverTransaction: boolean;
        getTransactionReceipt: boolean;
        updateInventoryTransactionStatus: boolean;
        updateTransactionStatus: boolean;
        getTransactionPricePerGramValue: boolean;
        // silver inventory
        getInventoryTransactionListing: boolean;
        getInventoryListing: boolean;
        createInventory: boolean;
        createTransactionDraft: boolean;
        transferInventory: boolean;
        exportInventoryTransactionListing: boolean;
        exportSilverTransactionListing: boolean;
        getTotalNetValueOrQuantity: boolean;
        // transfer
        getInventoryTransferListing: boolean;
        updateInventoryTransferStatus: boolean;
        getSerialNumberListing: boolean;

        //silver invenroty report
        getSilverInventoryReportReceivedListing: boolean;
        exportInventoryReceivedListing: boolean;
        getSilverInventoryReportDeliveredListing: boolean;
        exportInventoryDeliveredListing: boolean;
        getBranchInventoryListing: boolean;
        //conversion
        getSilverConversionListing: boolean;
        getSilverConversionAmount: boolean;
        createSilverConversion: boolean;
        createEarmarkConversion: boolean;
        getEarmarkConversionListing: boolean;
        updateEarmarkDetails: boolean;
        getInventoryUsage: boolean;
    };
    buyAndSellSilver: {
        transactionListing: {
            index: number;
            maxIndex: number;
            filters: TGetSilverTransactionListingFilters;
            data: TTransactionSilverListing[];
        };
        silverPrices: TSilverPrices;
        transactionType: TransactionActionEnum;
        selectedTransaction: TTransactionSilverListing | undefined;
    }
    silverInventory: {
        inventoryTransactionListing: {
            index: number;
            maxIndex: number;
            filters: TGetSilverInventoryTransactionListingFilters;
            data: TInventorySilverTransactionListing[];
        };
        inventoryListing: {
            index: number;
            maxIndex: number;
            filters: TGetSilverInventoryListingFilters;
            data: TGetSilverInventoryListing[];
            selectedTab: SilverInventorySelectedTab;
        };
        inventoryTransferListing: {
            index: number;
            maxIndex: number;
            filters: TGetInventoryTransferListingFilters;
            data: TInventoryTransferListing[];
        };
    };
    silverInventoryReport: {
        selectedTab: ReportsSilverInventorySelectedTab;
        branchInventory: ReportBranchSilverInventoryRedux;
        inventoryReceived: ReportSilverInventoryReceivedRedux;
        inventoryDelivered: ReportSilverInventoryReceivedRedux;
    },
    serialNumber: {
        serialNumberListing: TSerialNumberSilverListingReduxParams;
    };
    allProducts: TSilverProductFilter[];
    allInventories: TSilverInventoryFilter[];
    allSerialNumbers: TSerialNumberFilter[];
    branchInventories: TSilverInventoryFilter[];
    createTransactionSummary: TCreateSilverTransactionDraftResponse;
    params: {
        buyOrSellSilverParams: TBuyOrSellMultipleSilverParams;
        createInventoryParams: TCreateInventoryParamsV2;
        transferInventoryParams: TTransferInventoryParams;
        inventoryFilterParams: TGetInventoryFilterParams;
        serialNumberFilterParams: TGetSerialNumberFilterParams;
    }
    silverConversion: TSilverConversionRedux;
    earmarkConversionListing: {
        index: number;
        maxIndex: number;
        filters: TGetEarmarkConversionListingFilters;
        data: TGetEarmarkConversionListingAPIResponse['data'];
    };
    inventoryUsage: {
        totalInventoryStock: number;
        totalInventoryUsed: number;
        totalInventoryBalance: number;
    }
    modals: {
        isFormFilled: boolean;
        isConfirmationModalOpen: boolean;
        isBuyOrSellSilverModalOpen: boolean;
        isTransactionDetailsModalOpen: boolean;
        isInventoryListingModalOpen: boolean;
        isCreateInventoryModalOpen: boolean;
        isTransferInventoryModalOpen: boolean;
        isCreateSilverConversionModalOpen: boolean;
        confirmUpdateInventoryTransactionStatusModalIsOpen: boolean;
        confirmUpdateTransactionStatusModalIsOpen: boolean;
        confirmUpdateInventoryTransfersStatusModalIsOpen: boolean;
        transactionProductListingModalIsOpen: boolean;
    };
    tempUpdateInventoryTransactionState: TUpdateInventoryTransactionStatusReduxParams;
    tempUpdateTransactionState: TUpdateTransactionStatusReduxParams;
    tempUpdateInventoryTransfersState: TupdateTransferStatusReduxParams;

    inventoryTransactionListingForExport: TInventorySilverTransactionListing[];
    inventoryReceivedListingForExport: TInventorySilverTransactionListing[];
    inventoryDeliveredListingForExport: TInventorySilverTransactionListing[];
    silverTransactionListingForExport: TSilverTransactionListing[];
    errors: {
        getAllProducts: string;
        getAllInventories: string;
        getAllSerialNumbers: string;
        getBranchInventories: string;
        // buy and sell silver
        getTransactionListing: string;
        getSilverPrices: string;
        createBuySilverTransaction: string;
        createSellSilverTransaction: string;
        getTransactionReceipt: string;
        updateInventoryTransactionStatus: string;
        updateTransactionStatus: string;
        getTransactionPricePerGramValue: string;
        // silver inventory
        getInventoryTransactionListing: string;
        getInventoryListing: string;
        createInventory: string;
        createTransactionDraft: string;
        transferInventory: string;
        exportInventoryTransactionListing: string;
        exportSilverTransactionListing: string;
        getTotalNetValueOrQuantity: string;
        // transfer
        getInventoryTransferListing: string;
        updateInventoryTransferStatus: string;
        getSerialNumberListing: string;
        //silver inventory report
        getSilverInventoryReportReceivedListing: string;
        exportInventoryReceivedListing: string;
        getSilverInventoryReportDeliveredListing: string;
        exportInventoryDeliveredListing: string;
        getBranchInventoryListing: string;
        //conversion
        getSilverConversionListing: string;
        getSilverConversionAmount: string;
        createSilverConversion: string;
        createEarmarkConversion: string;
        getEarmarkConversionListing: string;
        updateEarmarkDetails: string;
        getInventoryUsage: string;
    };
}

export type ReportSilverInventoryReceivedRedux = {
    index: number,
    maxIndex: number,
    filters: ReportSilverInventoryReceivedListingFilters,
    data: TInventorySilverTransactionListing[],
}

export type ReportSilverInventoryReceivedListingFilters = {
    branchName: string;
    branchCode: string;
    fundType: string;
    productName: string;
    serialNo: string;
}

export type ReportBranchSilverInventoryRedux = {
    index: number;
    maxIndex: number;
    filters: {
        branchCode: string;
    };
    data: TGetSilverInventoryListing[];
}

// Navigation state
export type TNavigationReduxState = {
    currentTab: NavigationTabsEnum;
}

// Branch state
export type TBranchReduxState = {
    loadings: {
        getRegionListing: boolean;
        getBranchListing: boolean;
        createNewRegion: boolean;
        createNewBranch: boolean;
        getAllRegions: boolean;
        getAllBranches: boolean;
        editBranchDetails: boolean;
        editRegionDetails: boolean;
    };
    modals: {
        createNewRegionModalIsOpen: boolean;
        createNewBranchModalIsOpen: boolean;
        branchDetailsModalIsOpen: boolean;
        deleteBranchModalIsOpen: boolean;
        isEditing: boolean;
        regionDetailsModalIsOpen: boolean;
        regionIsEditing: boolean;
        deleteRegionModalIsOpen: boolean;
    };
    regionListing: {
        index: number;
        maxIndex: number;
        data: TRegion[];
    };
    branchListing: {
        index: number;
        maxIndex: number;
        filters: TGetBranchListingFilters;
        data: TBranch[];
    };
    selectedBranch: TBranch | undefined,
    selectedRegion: TRegion | undefined,
    allBranches: TBranchFilter[];
    allRegions: TRegionFilter[];
    errors: {
        getRegionListing: string;
        getBranchListing: string;
        createNewRegion: string;
        createNewBranch: string;
        getAllRegions: string;
        getAllBranches: string;
        editBranchDetails: string;
        editRegionDetails: string;
    };
}

export type TSettingReduxState = {
    loadings: {
        getSettings: boolean;
        updateSettings: boolean;
        createProduct: boolean;
        getApiKeySettings: boolean;
        updateApiKeySettings: boolean;
        getDiscounts: boolean;
        updateDiscount: boolean;
        createDiscount: boolean;
        getAllDiscount: boolean;
        getApiKeyHistoryListing: boolean;
        getSilverApiKeySettings: boolean;
        updateSilverApiKeySettings: boolean;
        getSilverApiKeyHistoryListing: boolean;
    };
    selectedTab: SettingTabsEnum;
    modals: {
        productSettingDetailsModalIsOpen: boolean;
        productSettingDetailsModalIsEditing: boolean;
        productSettingDeleteProductModalIsOpen: boolean;
        productSettingCreateProductModalIsOpen: boolean;
        productManagementDiscountCreateModalIsOpen: boolean;
        productManagementDiscountDetailsModalIsOpen: boolean;
        productManagementDiscountDetailsModalIsEditing: boolean;
        productManagementDiscountDeleteModalIsOpen: boolean;
        regenerateAPIKeyModalIsOpen: boolean;
        regenerateSilverAPIKeyModalIsOpen: boolean;
    };
    settings: {
        index: number;
        maxIndex: number;
        globalSettingsIsEditing: boolean;
        filters: TSettingsFilterParams;
        appliedMetalType: string;
        data: TGlobalAndProductSettings[];
    };
    apiKeySettings: TAPIKeySettingsReduxResponse;
    apiKeyHistoryListing: TAPIKeyHistoryListing;
    silverApiKeySettings: TAPIKeySettingsReduxResponse;
    silverApiKeyHistoryListing: TAPIKeyHistoryListing;
    productSettingDetails: TGlobalAndProductSettings;
    discounts: {
        index: number;
        maxIndex: number;
        data: TDiscountDetailsSettings[];
        filters: TDiscountListingFilters,
        selectedDiscount?: TDiscountDetailsSettings;
    };
    allDiscounts: TDiscountDetailsSettings[],
    isLoadingCreateAdminSettings: boolean;
    createAdminSettingsError: string;
    errors: {
        getSettings: string;
        updateSettings: string;
        createProduct: string;
        getApiKeySettings: string;
        updateApiKeySettings: string;
        getDiscounts: string;
        updateDiscount: string;
        createDiscount: string;
        getAllDiscount: string;
        getApiKeyHistoryListing: string;
        getSilverApiKeySettings: string;
        updateSilverApiKeySettings: string;
        getSilverApiKeyHistoryListing: string;
    };
}

export type TSettingsFilterParams = {
    productName: string;
    fundType: string;
    goldType: string;
    silverType: string;
    metalType: string;
}

export type TAPIKeyHistoryListing = {
    index: number;
    maxIndex: number;
    data: TAPIKeyHistoryListingData[];
}

export type TAPIKeyHistoryListingData = {
    id: number;
    apiKey: string;
    expiresAt: string;
    createdBy: number;
    isManualRenewal: boolean;
    createdAt: string;
    updatedAt: string;
}

// Admin state
export type TAdminReduxState = {
    loadings: {
        getAdminListing: boolean;
        createNewAdmin: boolean;
        editAdminDetails: boolean;
        deleteAdmin: boolean;
        getRoleListing: boolean;
        createNewRole: boolean;
        editRoleDetails: boolean;
        deleteRole: boolean;
        getAllRoles: boolean;
        exportAdminUserListing: boolean;
        exportAdminRoleListing: boolean;
    };
    modals: {
        createNewAdminModalIsOpen: boolean;
        adminDetailsModalIsOpen: boolean;
        adminDetailsModalIsEditing: boolean;
        deleteAdminModalIsOpen: boolean;
        createNewRoleModalIsOpen: boolean;
        roleDetailsModalIsOpen: boolean;
        roleDetailsModalIsEditing: boolean;
        deleteRoleModalIsOpen: boolean;
    };
    adminListing: {
        index: number;
        maxIndex: number;
        filters: TGetAdminListingFilters;
        data: TAdminListing[];
    };
    adminDetails: TAdminDetails,
    roleListing: {
        index: number;
        maxIndex: number;
        data: TRoleListing[];
    }
    roleDetails: TRoleListing,
    allRoles: TRoleFilter[];
    adminUserListingForExport: TExportAdminUserListingAPIResponse[];
    adminRoleListingForExport: TExportAdminRoleListingAPIResponse[],
    errors: {
        getAdminListing: string;
        createNewAdmin: string;
        editAdminDetails: string;
        deleteAdmin: string;
        getRoleListing: string;
        createNewRole: string;
        editRoleDetails: string;
        deleteRole: string;
        getAllRoles: string;
        exportAdminUserListing: string;
        exportAdminRoleListing: string;
    };
}

// Approval state
export type TApprovalReduxState = {
    loadings: {
        updateTransactionStatus: boolean;
        updateInventoryTransactionStatus: boolean;
        updateDiscountStatus: boolean;
        updateProductStatus: boolean;
        updateCollateralStatus: boolean;
        updateProjectStatus: boolean;
        updateTransferStatus: boolean;
        updateReleaseEarmarkStatus: boolean;
        updateBranchesStatus: boolean;
        updateGoldConversionStatus: boolean;
        updateEarmarkConversionStatus: boolean;
        updateStockOutStatus: boolean;

        getPendingTransactionListing: boolean;
        getPendingInventoryTransactionListing: boolean;
        getPendingDiscountListing: boolean;
        getPendingProductListing: boolean;
        getPendingCollateralListing: boolean;
        getPendingProjectListing: boolean;
        getpendingTransferListing: boolean;
        getPendingReleaseEarmarkListing: boolean;
        getPendingBranchesListing: boolean;
        getPendingGoldConversionListing: boolean;
        getPendingStockOutListing: boolean;
    };
    selectedTab: ApprovalTabsEnum;
    modals: {
        confirmUpdateTransactionStatusModalIsOpen: boolean;
        confirmUpdateInventoryTransactionStatusModalIsOpen: boolean;
        confirmUpdateDiscountStatusModalIsOpen: boolean;
        confirmUpdateProductStatusModalIsOpen: boolean;
        confirmUpdateCollateralStatusModalIsOpen: boolean;
        confirmUpdateProjectStatusModalIsOpen: boolean;
        confirmupdateTransferStatusModalIsOpen: boolean;
        confirmUpdateReleaseEarmarkModalIsOpen: boolean;
        confirmUpdateBranchesModalIsOpen: boolean;
        confirmUpdateGoldConversionModalIsOpen: boolean;
        confirmUpdateEarmarkConversionStatusModalIsOpen: boolean,
        confirmUpdateStockOutStatusModalIsOpen: boolean;
    };
    tempUpdateTransactionState: TUpdateTransactionStatusReduxParams;
    pendingTransactionListing: {
        index: number;
        maxIndex: number;
        filters: TGetTransactionListingFilters;
        data: TTransactionListing[];
    };
    tempUpdateInventoryTransactionState: TUpdateInventoryTransactionStatusReduxParams;
    pendingInventoryTransactionListing: {
        index: number;
        maxIndex: number;
        filters: TGetInventoryTransactionListingFilters;
        data: TInventoryTransactionListing[];
    },
    tempUpdateDiscountState: TUpdateDiscountStatusReduxParams;
    pendingDiscountListing: {
        index: number;
        maxIndex: number;
        filters: TPendingDiscountApprovalListingFilters;
        data: TPendingDiscountListing[];
    },
    tempUpdateProductState: TUpdateProductStatusReduxParams;
    pendingProductListing: {
        index: number;
        maxIndex: number;
        filters: {
            productName: string;
            metalType: string;
        },
        data: TGetProductChangeHistoryListing[];
    },
    tempUpdateCollateralState: TUpdateCollateralStatusReduxParams;
    pendingCollateralListing: {
        index: number;
        maxIndex: number;
        filters: TPendingCollateralApprovalListingFilters,
        data: TGetCollateralListing[];
    },
    tempUpdateProjectState: TUpdateProjectStatusReduxParams;
    pendingProjectListing: {
        index: number;
        maxIndex: number;
        filters: TPendingProjectApprovalListingFilters;
        data: TGetProjectChangeHistoryListing[];
    },
    tempUpdateTransferState: TupdateTransferStatusReduxParams;
    pendingTransferListing: {
        index: number;
        maxIndex: number;
        filters: TPendingTransfersApprovalListingFilters;
        data: TGetInventoryTransferListing[];
    },
    tempUpdateReleaseEarmark: TUpdateReleaseEarmarkStatusReduxParams;
    pendingReleaseEarmarkListing: {
        index: number;
        maxIndex: number;
        filters: TPendingReleaseEarmarkListingFilters;
        data: TGetReleaseEarmarkListing[];
    },
    pendingBranchesListing: {
        index: number;
        maxIndex: number;
        filters: TGetBranchListingFilters;
        data: TBranchChangeHistoryData[];
    },
    tempUpdateBranchesState: TUpdateBranchesStatusReduxParams;
    pendingGoldConversionListing: {
        index: number;
        maxIndex: number;
        filters: TGetPendingGoldConversionFiltersRedux;
        data: TGoldConversionListingData[];
    },
    tempUpdateGoldConversionState: TUpdateGoldConversionStatusReduxParams,
    tempUpdateEarmarkConversionState: TUpdateEarmarkConversionStatusReduxParams;
    tempUpdateStockOutState: TUpdateStockOutStatusReduxParams;
    pendingStockOutListing: {
        index: number;
        maxIndex: number;
        filters: TGetPendingStockOutListingFilters;
        data: TStockOutListing[];
    };
    errors: {
        updateTransactionStatus: string;
        updateInventoryTransactionStatus: string;
        updateDiscountStatus: string;
        updateProductStatus: string;
        updateCollateralStatus: string;
        updateProjectStatus: string;
        updateTransferStatus: string;
        updateReleaseEarmarkStatus: string;
        updateBranchesStatus: string;
        updateGoldConversionStatus: string;
        updateEarmarkConversionStatus: string;
        updateStockOutStatus: string;

        getPendingTransactionListing: string;
        getPendingInventoryTransactionListing: string;
        getPendingDiscountListing: string;
        getPendingProductListing: string;
        getPendingCollateralListing: string;
        getPendingProjectListing: string;
        getpendingTransferListing: string;
        getPendingReleaseEarmarkListing: string;
        getPendingBranchesListing: string;
        getPendingGoldConversionListing: string;
        getPendingStockOutListing: string;
    };
}

// Project state
export type TProjectReduxState = {
    loadings: {
        getProjectListing: boolean;
        createNewProject: boolean;
        editProjectDetails: boolean;
        getProjectDetails: boolean;
        getProjectFilters: boolean;
    };
    projectListing: {
        index: number;
        maxIndex: number;
        filters: TGetProjectListingFilters;
        data: TProjectListing[];
    };
    modals: {
        createNewProjectModalIsOpen: boolean;
        projectDetailsModalIsOpen: boolean;
        projectStatusProjectIsOpen: boolean;
        isEditing: boolean;
    };
    projectDetails: TProjectDetails;
    projectFilters: TGetProjectFilter[];
    errors: {
        getProjectListing: string;
        createNewProject: string;
        editProjectDetails: string;
        getProjectDetails: string;
        getProjectFilters: string;
    };
}

export enum ReportsGoldInventorySelectedTab {
    InventoryReceived = 1,
    InventoryDelivered = 2,
    BranchInventory = 3,
}

export enum ReportsSilverInventorySelectedTab {
    InventoryReceived = 1,
    InventoryDelivered = 2,
    BranchInventory = 3,
}
